import { useState, useEffect } from "react";
import { Filter, UserCheck, Tag as TagIcon, Target, Clock, CalendarPlus, Phone, Cake } from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../../../components/UI/Popover";
import { Checkbox } from "../../../../../components/UI/Checkbox";
import { Button } from "../../../../../components/UI/Button";
import { cn } from "../../../../../utils/utils";
import Avatar from "../../../../../components/UICustom/Avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../components/UI/Tooltip";

const CATEGORIES = [
  { id: "unread", label: "Chưa đọc" },
  { id: "stranger", label: "Người lạ" },
  { id: "friends", label: "Bạn bè" },
  { id: "groups", label: "Nhóm" },
  { id: "requesting", label: "Lời mời kết bạn" },
  { id: "birthday", label: "Sinh nhật" },
];

// Sidebar Filter Button component
const SidebarFilterButton = ({
  icon,
  title,
  label,
  active = false,
  badge,
  children,
  open,
  onOpenChange,
}) => {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  "relative inline-flex items-center gap-1 h-7 px-2.5 rounded-full border text-[12px] font-medium transition-all duration-200 whitespace-nowrap focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none",
                  active
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50",
                )}
                title={title}
              >
                {icon}
                {label && <span>{label}</span>}
                {badge > 0 && (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[10px] font-semibold text-white bg-red-500 rounded-full">
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>{title}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {children}
    </Popover>
  );
};

// Category Filter for Sidebar
const SidebarCategoryFilter = ({ selectedCategories, onCategoriesChange }) => {
  const [open, setOpen] = useState(false);
  const [localSelectedCategories, setLocalSelectedCategories] =
    useState(selectedCategories);

  useEffect(() => {
    setLocalSelectedCategories(selectedCategories);
  }, [selectedCategories]);

  const handleCategoryToggle = (categoryId) => {
    setLocalSelectedCategories((current) =>
      current.includes(categoryId)
        ? current.filter((id) => id !== categoryId)
        : [...current, categoryId],
    );
  };

  const handleApply = () => {
    onCategoriesChange(localSelectedCategories);
    setOpen(false);
  };

  const handleReset = () => {
    setLocalSelectedCategories([]);
  };

  return (
    <SidebarFilterButton
      icon={<Filter size={14} />}
      title="Lọc loại tin nhắn"
      label="Loại"
      active={selectedCategories.length > 0}
      badge={selectedCategories.length}
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverContent className="w-52 p-2.5 bg-white" side="bottom" align="start">
        <div className="space-y-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Chọn loại tin nhắn</div>
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            {CATEGORIES.map((category) => (
              <div key={category.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`sidebar-category-${category.id}`}
                  checked={localSelectedCategories.includes(category.id)}
                  onCheckedChange={() => handleCategoryToggle(category.id)}
                />
                <label
                  htmlFor={`sidebar-category-${category.id}`}
                  className="flex items-center cursor-pointer flex-1 text-[12px]"
                >
                  {category.label}
                </label>
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            <Button variant="outline" size="sm" className="h-7 text-[12px]" onClick={handleReset}>
              Xóa
            </Button>
            <Button size="sm" className="h-7 text-[12px]" onClick={handleApply}>
              Áp dụng
            </Button>
          </div>
        </div>
      </PopoverContent>
    </SidebarFilterButton>
  );
};

// Manager Filter for Sidebar
const SidebarManagerFilter = ({
  managers,
  selectedManagers,
  onManagersChange,
}) => {
  const [open, setOpen] = useState(false);
  const [localSelectedManagers, setLocalSelectedManagers] =
    useState(selectedManagers);

  useEffect(() => {
    setLocalSelectedManagers(selectedManagers);
  }, [selectedManagers]);

  const handleManagerToggle = (managerId) => {
    setLocalSelectedManagers((current) =>
      current.includes(managerId)
        ? current.filter((id) => id !== managerId)
        : [...current, managerId],
    );
  };

  const handleApply = () => {
    onManagersChange(localSelectedManagers);
    setOpen(false);
  };

  const handleReset = () => {
    setLocalSelectedManagers([]);
  };

  return (
    <SidebarFilterButton
      icon={<UserCheck size={14} />}
      title="Lọc theo quản lý"
      label="Quản lý"
      active={selectedManagers.length > 0}
      badge={selectedManagers.length}
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverContent className="w-52 p-2.5 bg-white" side="bottom" align="start">
        <div className="space-y-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Chọn quản lý</div>
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sidebar-manager-0"
                checked={localSelectedManagers.includes(0)}
                onCheckedChange={() => handleManagerToggle(0)}
              />
              <label
                htmlFor="sidebar-manager-0"
                className="flex items-center cursor-pointer flex-1 text-[12px]"
              >
                <Avatar user={null} size="sm" className="mr-2" />
                Không có quản lý
              </label>
            </div>
            {managers?.map((manager) => (
              <div key={manager._id} className="flex items-center space-x-2">
                <Checkbox
                  id={`sidebar-manager-${manager._id}`}
                  checked={localSelectedManagers.includes(manager._id)}
                  onCheckedChange={() => handleManagerToggle(manager._id)}
                />
                <label
                  htmlFor={`sidebar-manager-${manager._id}`}
                  className="flex items-center cursor-pointer flex-1 text-[12px]"
                >
                  <Avatar
                    user={manager}
                    size="sm"
                    className="mr-2 border-2 border-primary/30"
                  />
                  {manager.name}
                </label>
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            <Button variant="outline" size="sm" className="h-7 text-[12px]" onClick={handleReset}>
              Xóa
            </Button>
            <Button size="sm" className="h-7 text-[12px]" onClick={handleApply}>
              Áp dụng
            </Button>
          </div>
        </div>
      </PopoverContent>
    </SidebarFilterButton>
  );
};

// Tag Filter for Sidebar
const SidebarTagFilter = ({ tags, selectedTags, onTagsChange }) => {
  const [open, setOpen] = useState(false);
  const [localSelectedTags, setLocalSelectedTags] = useState(selectedTags);
  const [selectAll, setSelectAll] = useState(selectedTags.length === 0);

  useEffect(() => {
    setLocalSelectedTags(selectedTags);
    setSelectAll(selectedTags.length === 0);
  }, [selectedTags]);

  const handleTagToggle = (tagId) => {
    setLocalSelectedTags((current) => {
      const newSelection = current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId];
      setSelectAll(newSelection.length === 0);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) return;
    setSelectAll(true);
    setLocalSelectedTags([]);
  };

  const handleApply = () => {
    onTagsChange(localSelectedTags);
    setOpen(false);
  };

  const handleReset = () => {
    setLocalSelectedTags([]);
    setSelectAll(true);
  };

  return (
    <SidebarFilterButton
      icon={<TagIcon size={14} />}
      title="Tag (tự cài đặt)"
      label="Tag"
      active={selectedTags.length > 0}
      badge={selectedTags.length}
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverContent className="w-52 p-2.5 bg-white" side="bottom" align="start">
        <div className="space-y-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Chọn nhãn</div>
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            <div className="flex items-center space-x-2 pb-1 border-b border-gray-100">
              <Checkbox
                id="sidebar-tag-all"
                checked={selectAll}
                onCheckedChange={handleSelectAll}
              />
              <label
                htmlFor="sidebar-tag-all"
                className="flex items-center cursor-pointer flex-1 text-[12px] font-medium"
              >
                Tất cả
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="sidebar-tag-0"
                checked={localSelectedTags.includes(0)}
                onCheckedChange={() => handleTagToggle(0)}
              />
              <label
                htmlFor="sidebar-tag-0"
                className="flex items-center cursor-pointer flex-1 text-[12px]"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full mr-2 border-2 border-gray-400"
                  style={{ backgroundColor: "white" }}
                ></span>
                Không gắn nhãn
              </label>
            </div>

            {tags?.map((tag) => (
              <div key={tag._id} className="flex items-center space-x-2">
                <Checkbox
                  id={`sidebar-tag-${tag._id}`}
                  checked={localSelectedTags.includes(tag._id)}
                  onCheckedChange={() => handleTagToggle(tag._id)}
                />
                <label
                  htmlFor={`sidebar-tag-${tag._id}`}
                  className="flex items-center cursor-pointer flex-1 text-[12px]"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full mr-2"
                    style={{ backgroundColor: tag.color }}
                  ></span>
                  {tag.name}
                </label>
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            <Button variant="outline" size="sm" className="h-7 text-[12px]" onClick={handleReset}>
              Xóa
            </Button>
            <Button size="sm" className="h-7 text-[12px]" onClick={handleApply}>
              Áp dụng
            </Button>
          </div>
        </div>
      </PopoverContent>
    </SidebarFilterButton>
  );
};

// Tag Zalo Filter for Sidebar
const SidebarTagZaloFilter = ({
  tagZalos,
  selectedTagZalos,
  onTagZalosChange,
}) => {
  const [open, setOpen] = useState(false);
  const [localSelected, setLocalSelected] = useState(selectedTagZalos);
  const [selectAll, setSelectAll] = useState(selectedTagZalos.length === 0);

  useEffect(() => {
    setLocalSelected(selectedTagZalos);
    setSelectAll(selectedTagZalos.length === 0);
  }, [selectedTagZalos]);

  const handleToggle = (id) => {
    setLocalSelected((current) => {
      const newSelection = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      setSelectAll(newSelection.length === 0);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) return;
    setSelectAll(true);
    setLocalSelected([]);
  };

  const handleApply = () => {
    onTagZalosChange(localSelected);
    setOpen(false);
  };

  const handleReset = () => {
    setLocalSelected([]);
    setSelectAll(true);
  };

  // Use a different icon color for Tag Zalo to distinguish from regular Tag
  return (
    <SidebarFilterButton
      icon={
        <div className="relative">
          <TagIcon size={14} className="text-[#0068ff]" />
          <span className="absolute -bottom-1 -right-1 w-1.5 h-1.5 bg-[#0068ff] rounded-full" />
        </div>
      }
      title="Tag Zalo (hệ thống Zalo)"
      label="Tag Zalo"
      active={selectedTagZalos.length > 0}
      badge={selectedTagZalos.length}
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverContent className="w-52 p-2.5 bg-white" side="bottom" align="start">
        <div className="space-y-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Chọn Tag Zalo</div>
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            <div className="flex items-center space-x-2 pb-1 border-b border-gray-100">
              <Checkbox
                id="sidebar-tagzalo-all"
                checked={selectAll}
                onCheckedChange={handleSelectAll}
              />
              <label
                htmlFor="sidebar-tagzalo-all"
                className="flex items-center cursor-pointer flex-1 text-[12px] font-medium"
              >
                Tất cả
              </label>
            </div>
            {tagZalos?.map((tag) => (
              <div key={tag._id} className="flex items-center space-x-2">
                <Checkbox
                  id={`sidebar-tagzalo-${tag._id}`}
                  checked={localSelected.includes(tag._id)}
                  onCheckedChange={() => handleToggle(tag._id)}
                />
                <label
                  htmlFor={`sidebar-tagzalo-${tag._id}`}
                  className="flex items-center cursor-pointer flex-1 text-[12px]"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full mr-2"
                    style={{ backgroundColor: tag.color }}
                  ></span>
                  {tag.text}
                </label>
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            <Button variant="outline" size="sm" className="h-7 text-[12px]" onClick={handleReset}>
              Xóa
            </Button>
            <Button size="sm" className="h-7 text-[12px]" onClick={handleApply}>
              Áp dụng
            </Button>
          </div>
        </div>
      </PopoverContent>
    </SidebarFilterButton>
  );
};

// Sort Mode Filter for Sidebar
const SidebarSortFilter = ({ sortMode, onSortModeChange }) => {
  const [open, setOpen] = useState(false);

  const SORT_OPTIONS = [
    { id: "recent", label: "Mới nhất" },
    { id: "unread_first", label: "Chưa đọc trước" },
  ];

  const handleSelect = (mode) => {
    onSortModeChange(mode);
    setOpen(false);
  };

  const currentLabel =
    SORT_OPTIONS.find((o) => o.id === sortMode)?.label || "Mới nhất";

  return (
    <SidebarFilterButton
      icon={
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m3 16 4 4 4-4" />
          <path d="M7 20V4" />
          <path d="M11 4h10" />
          <path d="M11 8h7" />
          <path d="M11 12h4" />
        </svg>
      }
      title={`Sắp xếp: ${currentLabel}`}
      label="Sắp xếp"
      active={sortMode === "unread_first"}
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverContent className="w-44 p-2 bg-white" side="bottom" align="start">
        <div className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-1">Sắp xếp theo</div>
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.id}
              className={cn(
                "w-full text-left px-2.5 py-1.5 text-[12px] rounded-md transition-colors",
                sortMode === option.id
                  ? "bg-primary text-white"
                  : "hover:bg-secondary",
              )}
              onClick={() => handleSelect(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </SidebarFilterButton>
  );
};

// Pipeline Stage filter — pick which sales stages to show
const SidebarPipelineStageFilter = ({
  stages = [],
  selectedStageIds = [],
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(selectedStageIds);
  useEffect(() => setLocal(selectedStageIds), [selectedStageIds]);

  const toggle = (id) => {
    setLocal((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );
  };
  const apply = () => {
    onChange?.(local);
    setOpen(false);
  };
  const reset = () => setLocal([]);

  return (
    <SidebarFilterButton
      icon={<Target size={14} />}
      title="Lọc theo giai đoạn bán hàng"
      label="Giai đoạn"
      active={selectedStageIds.length > 0}
      badge={selectedStageIds.length}
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverContent className="w-52 p-2.5 bg-white" side="bottom" align="start">
        <div className="space-y-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Chọn giai đoạn
          </div>
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sidebar-stage-0"
                checked={local.includes(0)}
                onCheckedChange={() => toggle(0)}
              />
              <label
                htmlFor="sidebar-stage-0"
                className="flex items-center cursor-pointer flex-1 text-[12px]"
              >
                <span className="w-2.5 h-2.5 rounded-full mr-2 border-2 border-gray-400 bg-white" />
                Chưa gắn giai đoạn
              </label>
            </div>
            {stages.map((s) => {
              const id = s._id || s.id;
              return (
                <div key={id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`sidebar-stage-${id}`}
                    checked={local.includes(id)}
                    onCheckedChange={() => toggle(id)}
                  />
                  <label
                    htmlFor={`sidebar-stage-${id}`}
                    className="flex items-center cursor-pointer flex-1 text-[12px]"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full mr-2"
                      style={{ backgroundColor: s.color || "#9ca3af" }}
                    />
                    {s.name}
                  </label>
                </div>
              );
            })}
            {stages.length === 0 && (
              <div className="text-[12px] text-gray-500 py-1">
                Chưa có giai đoạn nào
              </div>
            )}
          </div>
          <div className="flex justify-between">
            <Button variant="outline" size="sm" className="h-7 text-[12px]" onClick={reset}>
              Xóa
            </Button>
            <Button size="sm" className="h-7 text-[12px]" onClick={apply}>
              Áp dụng
            </Button>
          </div>
        </div>
      </PopoverContent>
    </SidebarFilterButton>
  );
};

// SLA / Waiting time filter — "chờ phản hồi > X"
const SLA_OPTIONS = [
  { value: null, label: "Không lọc" },
  { value: 15, label: "> 15 phút" },
  { value: 60, label: "> 1 giờ" },
  { value: 180, label: "> 3 giờ" },
  { value: 1440, label: "> 1 ngày" },
  { value: 4320, label: "> 3 ngày" },
];

const SidebarSLAFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const current = SLA_OPTIONS.find((o) => o.value === (value || null));
  const handlePick = (v) => {
    onChange?.(v);
    setOpen(false);
  };

  return (
    <SidebarFilterButton
      icon={<Clock size={14} />}
      title="Lọc theo thời gian chờ phản hồi"
      label="Chờ"
      active={!!value}
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverContent className="w-44 p-2 bg-white" side="bottom" align="start">
        <div className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-1">
            Khách đã chờ
          </div>
          {SLA_OPTIONS.map((opt) => (
            <button
              key={String(opt.value)}
              className={cn(
                "w-full text-left px-2.5 py-1.5 text-[12px] rounded-md transition-colors",
                (value || null) === opt.value
                  ? "bg-primary text-white"
                  : "hover:bg-secondary",
              )}
              onClick={() => handlePick(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </SidebarFilterButton>
  );
};

// New customer date range filter — "khách mới trong X ngày"
const NEW_CUSTOMER_OPTIONS = [
  { value: null, label: "Tất cả" },
  { value: 1, label: "Hôm nay" },
  { value: 7, label: "7 ngày" },
  { value: 30, label: "30 ngày" },
  { value: 90, label: "90 ngày" },
];

const SidebarNewCustomerFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const handlePick = (v) => {
    onChange?.(v);
    setOpen(false);
  };
  const current = NEW_CUSTOMER_OPTIONS.find((o) => o.value === (value || null));

  return (
    <SidebarFilterButton
      icon={<CalendarPlus size={14} />}
      title="Lọc theo khách mới tạo trong"
      label={value ? `Mới ${current?.label || ""}` : "Khách mới"}
      active={!!value}
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverContent className="w-44 p-2 bg-white" side="bottom" align="start">
        <div className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-1">
            Khách tạo trong
          </div>
          {NEW_CUSTOMER_OPTIONS.map((opt) => (
            <button
              key={String(opt.value)}
              className={cn(
                "w-full text-left px-2.5 py-1.5 text-[12px] rounded-md transition-colors",
                (value || null) === opt.value
                  ? "bg-primary text-white"
                  : "hover:bg-secondary",
              )}
              onClick={() => handlePick(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </SidebarFilterButton>
  );
};

// Contact info qualification filter — phone/email status
const CONTACT_INFO_OPTIONS = [
  { value: null, label: "Tất cả" },
  { value: "any", label: "Có bất kỳ (phone/email)" },
  { value: "phone", label: "Có phone" },
  { value: "email", label: "Có email" },
  { value: "none", label: "Chưa có gì" },
];

const SidebarContactInfoFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const current = CONTACT_INFO_OPTIONS.find((o) => o.value === (value ?? null));
  const handlePick = (v) => {
    onChange?.(v);
    setOpen(false);
  };

  return (
    <SidebarFilterButton
      icon={<Phone size={14} />}
      title="Lọc theo thông tin liên hệ"
      label={value ? current?.label || "Liên hệ" : "Liên hệ"}
      active={!!value}
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverContent className="w-52 p-2 bg-white" side="bottom" align="start">
        <div className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-1">
            Thông tin liên hệ
          </div>
          {CONTACT_INFO_OPTIONS.map((opt) => (
            <button
              key={String(opt.value)}
              className={cn(
                "w-full text-left px-2.5 py-1.5 text-[12px] rounded-md transition-colors",
                (value ?? null) === opt.value
                  ? "bg-primary text-white"
                  : "hover:bg-secondary",
              )}
              onClick={() => handlePick(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </SidebarFilterButton>
  );
};

// Upcoming birthday filter — sinh nhật trong X ngày tới
const UPCOMING_BIRTHDAY_OPTIONS = [
  { value: null, label: "Không lọc" },
  { value: 3, label: "Trong 3 ngày tới" },
  { value: 7, label: "Trong 7 ngày tới" },
  { value: 30, label: "Trong 30 ngày tới" },
];

const SidebarUpcomingBirthdayFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const current = UPCOMING_BIRTHDAY_OPTIONS.find(
    (o) => o.value === (value ?? null),
  );
  const handlePick = (v) => {
    onChange?.(v);
    setOpen(false);
  };

  return (
    <SidebarFilterButton
      icon={<Cake size={14} />}
      title="Lọc theo sinh nhật sắp tới"
      label={value ? `SN ${value}d` : "SN sắp tới"}
      active={!!value}
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverContent className="w-52 p-2 bg-white" side="bottom" align="start">
        <div className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-1">
            Sinh nhật sắp tới
          </div>
          {UPCOMING_BIRTHDAY_OPTIONS.map((opt) => (
            <button
              key={String(opt.value)}
              className={cn(
                "w-full text-left px-2.5 py-1.5 text-[12px] rounded-md transition-colors",
                (value ?? null) === opt.value
                  ? "bg-primary text-white"
                  : "hover:bg-secondary",
              )}
              onClick={() => handlePick(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </SidebarFilterButton>
  );
};

// Main SidebarFilters component
const SidebarFilters = ({
  tags,
  tagZalos,
  managers,
  pipelineStages = [],
  selectedTags,
  selectedTagZalos,
  selectedManagers,
  selectedCategories,
  selectedPipelineStageIds = [],
  needsReplyMinutes,
  newCustomerDays,
  hasContactInfo,
  upcomingBirthdayDays,
  onTagsChange,
  onTagZalosChange,
  onManagersChange,
  onCategoriesChange,
  onPipelineStagesChange,
  onNeedsReplyMinutesChange,
  onNewCustomerDaysChange,
  onHasContactInfoChange,
  onUpcomingBirthdayDaysChange,
  sortMode,
  onSortModeChange,
}) => {
  return (
    <div className="flex flex-row items-center flex-wrap gap-1.5">
      <SidebarSortFilter
        sortMode={sortMode}
        onSortModeChange={onSortModeChange}
      />
      <SidebarCategoryFilter
        selectedCategories={selectedCategories}
        onCategoriesChange={onCategoriesChange}
      />
      <SidebarPipelineStageFilter
        stages={pipelineStages}
        selectedStageIds={selectedPipelineStageIds}
        onChange={onPipelineStagesChange}
      />
      <SidebarSLAFilter
        value={needsReplyMinutes}
        onChange={onNeedsReplyMinutesChange}
      />
      <SidebarNewCustomerFilter
        value={newCustomerDays}
        onChange={onNewCustomerDaysChange}
      />
      <SidebarContactInfoFilter
        value={hasContactInfo}
        onChange={onHasContactInfoChange}
      />
      <SidebarUpcomingBirthdayFilter
        value={upcomingBirthdayDays}
        onChange={onUpcomingBirthdayDaysChange}
      />
      <SidebarManagerFilter
        managers={managers}
        selectedManagers={selectedManagers}
        onManagersChange={onManagersChange}
      />
      <SidebarTagFilter
        tags={tags}
        selectedTags={selectedTags}
        onTagsChange={onTagsChange}
      />
      <SidebarTagZaloFilter
        tagZalos={tagZalos}
        selectedTagZalos={selectedTagZalos}
        onTagZalosChange={onTagZalosChange}
      />
    </div>
  );
};

export default SidebarFilters;
